// Lesson results (EDU-8, ARCH §29.6, §29.13). PURE (ENG-1).
//
// Results live on the PLAYER row (`PlayerState.lessons`), beside heredity's
// notebook (D-TF-135) — no new store. The world row carries its own active lesson
// (`WorldMeta.lesson`) and log (`WorldMeta.lessonLog`). This module owns their
// shape and their load-time sanitizers (save v5, M51.5), matching
// `heredity/notebook.ts`'s split: untrusted input is dropped, never thrown on
// (E-33), so an imported or hand-edited save always loads.
import { EDU } from '../config';
import type { Trigger } from '../game/advancements';
import type { ObjectiveTracker } from './objectives';
import type { LessonDef } from '../registry/schemas';

/** One player's progress through one lesson (§29.6). `objectives` maps each
 *  objective name to its completion tick, or `null` while still in progress —
 *  present for every objective the lesson declares, not only the finished ones,
 *  so a `k/n` progress read never needs the lesson def at hand. */
export interface LessonResult {
  started: number;
  objectives: Record<string, number | null>;
  counts: readonly (readonly [string, number])[];
  quiz?: { best: number; n: number; attempts: number; last: number };
  ticksOnTask: number;
  completed?: number;
}

/** The world's single active lesson (§29.3). Absent ⇒ the evaluator is not
 *  registered — parity by construction. */
export interface WorldLesson {
  name: string;
  collection: string;
  version: string;
  startedTick: number;
}

/** One instructor-console action, appended to the world's lesson log (§29.8). */
export interface LessonLogEntry {
  tick: number;
  instructor: string;
  action: string;
  target: string;
}

const int = (v: unknown): v is number => Number.isInteger(v);
const str = (v: unknown): v is string => typeof v === 'string';
const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function sanitizeQuizBest(v: unknown): LessonResult['quiz'] | undefined {
  if (!isObj(v)) return undefined;
  if (!int(v['best']) || !int(v['n']) || !int(v['attempts']) || !int(v['last'])) return undefined;
  return { best: v['best'], n: v['n'], attempts: v['attempts'], last: v['last'] };
}

function sanitizeOneResult(v: unknown): LessonResult | null {
  if (!isObj(v)) return null;
  if (!int(v['started']) || !int(v['ticksOnTask'])) return null;
  const objectives: Record<string, number | null> = {};
  if (isObj(v['objectives'])) {
    for (const [k, val] of Object.entries(v['objectives'])) {
      if (val === null) objectives[k] = null;
      else if (int(val)) objectives[k] = val;
    }
  }
  const counts: [string, number][] = Array.isArray(v['counts'])
    ? v['counts'].filter((c): c is [string, number] => Array.isArray(c) && c.length === 2 && str(c[0]) && int(c[1]))
    : [];
  const out: LessonResult = { started: v['started'], objectives, counts, ticksOnTask: v['ticksOnTask'] };
  const quiz = sanitizeQuizBest(v['quiz']);
  if (quiz) out.quiz = quiz;
  if (int(v['completed'])) out.completed = v['completed'];
  return out;
}

/** A player's `lessons` map from untrusted input: malformed rows dropped, never a
 *  throw. Absent or not an object ⇒ undefined — the pre-v5 absence, so a player
 *  who never joined a lesson round-trips unchanged. */
export function sanitizeLessons(raw: unknown): Record<string, LessonResult> | undefined {
  if (!isObj(raw)) return undefined;
  const out: Record<string, LessonResult> = {};
  let n = 0;
  for (const [name, v] of Object.entries(raw)) {
    const rec = sanitizeOneResult(v);
    if (rec) { out[name] = rec; n++; }
  }
  return n === 0 ? undefined : out;
}

export function sanitizeWorldLesson(raw: unknown): WorldLesson | undefined {
  if (!isObj(raw)) return undefined;
  if (!str(raw['name']) || !str(raw['collection']) || !str(raw['version']) || !int(raw['startedTick'])) return undefined;
  return { name: raw['name'], collection: raw['collection'], version: raw['version'], startedTick: raw['startedTick'] };
}

/** A world's `lessonLog` from untrusted input, capped at `EDU.LOG_MAX` (matching
 *  the live log's own cap, §29.8) — malformed entries dropped. */
export function sanitizeLessonLog(raw: unknown): LessonLogEntry[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: LessonLogEntry[] = [];
  for (const v of raw) {
    if (out.length >= EDU.LOG_MAX) break;
    if (!isObj(v)) continue;
    if (int(v['tick']) && str(v['instructor']) && str(v['action']) && str(v['target'])) {
      out.push({ tick: v['tick'], instructor: v['instructor'], action: v['action'], target: v['target'] });
    }
  }
  return out.length === 0 ? undefined : out;
}

// ── M51.7 — the evaluator's host-facing seams (EDU-3, EDU-9; ARCH §29.4) ────────
// Pure, so the "only while active" and "no active lesson ⇒ no allocation" (E-59)
// claims are unit-testable directly — `boot.ts` (imports `three`) is not.

/** A freshly started lesson result: every objective present and not yet done
 *  (§29.6 — "present for every objective... not only the finished ones"). */
export function emptyLessonResult(lessonDef: LessonDef, startedTick: number): LessonResult {
  const objectives: Record<string, number | null> = {};
  for (const o of lessonDef.objectives) objectives[o.name] = null;
  return { started: startedTick, objectives, counts: [], ticksOnTask: 0 };
}

/** Advance `ticksOnTask` by one — the only clock this module's own state keeps —
 *  IF a lesson is active (the host's own "is one running, and is the player in
 *  THIS world" check). Pure: inactive returns the SAME reference, not a copy. */
export function tickLessonResult(result: LessonResult, active: boolean): LessonResult {
  return active ? { ...result, ticksOnTask: result.ticksOnTask + 1 } : result;
}

/** Apply a trigger to a lesson's live `ObjectiveTracker`, folding its newly-
 *  completed objectives (and their tick) into the SAVED result — the tracker is
 *  the source of truth for progress; this only mirrors it into persisted shape.
 *  No active lesson, no def, or no result row (a player who never joined one) ⇒
 *  the SAME `result` reference back, unchanged, and `tracker.fire` never called —
 *  "ignored, no allocation" (E-59), not merely "no visible effect". Returns the
 *  titles of any objectives that just completed, for the host to toast. */
export function applyEduTrigger(
  t: Trigger, tracker: ObjectiveTracker | undefined, lessonDef: LessonDef | undefined,
  result: LessonResult | undefined, tick: number,
): { result: LessonResult | undefined; newlyTitles: string[] } {
  if (!tracker || !lessonDef || !result) return { result, newlyTitles: [] };
  const newly = tracker.fire(t);
  if (newly.length === 0) return { result: { ...result, counts: [...tracker.counts] }, newlyTitles: [] };
  const objectives = { ...result.objectives };
  for (const name of newly) objectives[name] = tick;
  const allDone = lessonDef.objectives.every((o) => objectives[o.name] !== null && objectives[o.name] !== undefined);
  const next: LessonResult = { ...result, objectives, counts: [...tracker.counts] };
  if (allDone) next.completed = result.completed ?? tick;
  const newlyTitles = newly.map((name) => lessonDef.objectives.find((o) => o.name === name)?.title ?? name);
  return { result: next, newlyTitles };
}
